import { WorkStepType, } from "./wsi4_interfaces";
import { getKeysOfObject, isArray, isBoolean, isNumber, isObject, isString, isStringIndexedInterface, } from "./wsi4_typeguards";
export function isVertexKey(arg) { return isString(arg); }
export function isRootIdKey(arg) { return isString(arg); }
export function isNodeIdKey(arg) { return isString(arg); }
export function clone(obj, typeGuard) {
    const data = JSON.parse(JSON.stringify(obj));
    if (!typeGuard(data)) {
        throw Error("Wrong type.");
    }
    return data;
}
export function objEq(lhs, rhs) {
    return JSON.stringify(lhs) === JSON.stringify(rhs);
}
function hasNode(node, article) {
    return article.nodes.some((n) => n.vertex === node);
}
function parentArticle(article, articles) {
    const externalTargets = article.nodes.reduce((res, node) => [
        ...res,
        ...node.targets.filter((targetNode) => !hasNode(targetNode, article)),
    ], []);
    if (externalTargets.length > 1) {
        return undefined;
    }
    return articles.find((candidate) => externalTargets.find((candidateNode) => hasNode(candidateNode, candidate)) !== undefined);
}
export function isSheetOnlyArticle(article) {
    return article.nodes.length === 1 && (article.nodes[0].workStepType === WorkStepType.sheet || article.nodes[0].workStepType === WorkStepType.tube);
}
function isChild(article, otherArticle, articles) {
    if (isSheetOnlyArticle(article)) {
        return false;
    }
    const pa = parentArticle(article, articles);
    if (pa === undefined) {
        return false;
    }
    else if (pa.nodes[0].vertex === otherArticle.nodes[0].vertex) {
        return true;
    }
    else {
        return isChild(pa, otherArticle, articles);
    }
}
export function childArticles(article, articles) {
    return articles.filter((candidate) => isChild(candidate, article, articles))
        .map(a => articleWithSheet(a, articles));
}
export function articleWithSheet(article, articles) {
    if (childArticles(article, articles).length === 0) {
        if (article.nodes.some(n => n.workStepType === WorkStepType.userDefinedBase)) {
            return article;
        }
        if (article.nodes.some(n => n.workStepType === WorkStepType.sheet)) {
            return article;
        }
        const cutting = article.nodes.find(n => n.workStepType === WorkStepType.sheetCutting);
        if (cutting === undefined) {
            return article;
        }
        const s = articles.find(a => a.nodes.length === 1 && cutting.sources.some(s => a.nodes[0].vertex === s));
        if (s === undefined) {
            throw Error("Missing sheet workStep");
        }
        return {
            ...article,
            nodes: [
                s.nodes[0],
                ...article.nodes,
            ],
        };
    }
    return article;
}
export function createPngImage(png) {
    return "data:image/png;base64," + png;
}
export function interestingNode(article) {
    if (article.nodes.length === 0) {
        throw Error("No nodes in article");
    }
    const node = Array.from(article.nodes)
        .reverse()
        .find(n => n.workStepType !== WorkStepType.userDefined);
    if (node === undefined) {
        throw Error("Only userDefined nodes in article");
    }
    return node;
}
export function convertEntry(entry) {
    if (isNumber(entry)) {
        return entry;
    }
    if (isString(entry)) {
        return entry;
    }
    if (isBoolean(entry)) {
        return entry;
    }
    return convertNullEntrysToUndefined(entry);
}
export function convertNullEntrysToUndefined(obj) {
    if (typeof obj !== "object") {
        throw Error("convertNullToUndefined: Wrong type");
    }
    if (obj === null) {
        return undefined;
    }
    if (Array.isArray(obj)) {
        const newArray = [];
        for (const entry of obj) {
            newArray.push(convertEntry(entry));
        }
        return newArray;
    }
    else {
        const newObject = {};
        for (const key of getKeysOfObject(obj)) {
            newObject[key] = convertEntry(obj[key]);
        }
        return newObject;
    }
}
export function emptyGraph() {
    return {
        creator: "",
        projectName: "",
        articles: [],
        resources: {
            attachments: {},
            gltfs: {},
            pngs: {},
            svgs: {},
            technicalDrawings: {},
            sheetTappingNodeData: [],
            tubeCuttingData: [],
            canForceSheetPartData: [],
            problematicGeometryGlfts: {},
        },
        minCompletionTime: 0,
        netSellingPrice: undefined,
    };
}
export function emptyShopConfig() {
    return {
        allowedAdditionalProcesses: [],
        allowedCuttingGasConfigurations: [],
        laserSheetCuttingMaterial: [],
        laserSheetCuttingMinAreas: [],
        material: [],
        minLowerDieOpeningWidths: [],
        minimalScrewDiameter: 0.,
        settings: {
            fixedRotationsEnabled: false,
            testReportRequired: false,
        },
        sheets: [],
        tubeCuttingProcesses: [],
        tubeMaterials: [],
        tubeProfiles: [],
        tubeSpecifications: [],
        tubes: [],
    };
}
export function articleSignatureNodeObject(article) {
    if (article.nodes.length === 0) {
        throw Error("No nodes in article");
    }
    const node = Array.from(article.nodes)
        .reverse()
        .find(n => n.workStepType !== WorkStepType.userDefined);
    if (node === undefined) {
        throw Error("Only userDefined nodes in article");
    }
    return node;
}
export function changeEntry(array, index, newValue, entryTypeguard) {
    const cl = clone(array, (t) => isArray(t, entryTypeguard));
    if (!isArray(cl, entryTypeguard)) {
        throw Error("Error in cloning");
    }
    cl[index] = newValue;
    return cl;
}
export const emptyTableEntry = "-";
export function isInstanceOf(arg, typeGuardMap) {
    return isStringIndexedInterface(arg) && getKeysOfObject(typeGuardMap)
        .every(key => typeGuardMap[key](arg[key]));
}
function compareStrings(lhs, rhs) {
    if (lhs < rhs) {
        return -1;
    }
    else if (rhs < lhs) {
        return 1;
    }
    else {
        return 0;
    }
}
function isEqualArray(first, second) {
    if (first.length !== second.length) {
        return false;
    }
    for (const index in first) {
        if (!isEqual(first[index], second[index])) {
            return false;
        }
    }
    return true;
}
export function isEqual(first, second) {
    if (typeof first !== typeof second) {
        return false;
    }
    if (first === undefined) {
        return true;
    }
    if (first === null) {
        return second === null;
    }
    if (second === null) {
        return first === null;
    }
    if (isNumber(first) || isBoolean(first) || isString(first)) {
        return first === second;
    }
    if (!isObject(first)) {
        throw Error("first: wrong type");
    }
    if (!isObject(second)) {
        throw Error("second: wrong type");
    }
    if (Array.isArray(first)) {
        if (!Array.isArray(second)) {
            throw Error("second: not array");
        }
        return isEqualArray(first, second);
    }
    const propertiesMatchKeys = (obj) => isEqualArray(Object.keys(obj)
        .sort(compareStrings), Object.getOwnPropertyNames(obj)
        .filter(o => !o.startsWith("__"))
        .sort(compareStrings));
    if (!propertiesMatchKeys(first) || !propertiesMatchKeys(second)) {
        throw Error("Object invalid: properties don't match");
    }
    const isValidPrototype = (obj) => JSON.stringify(Object.getPrototypeOf(obj)) === "{}";
    if (!isValidPrototype(first) || !isValidPrototype(second)) {
        throw Error("Object prototype invalid");
    }
    const emptyKeysMeansEmptyObject = (obj) => (Object.keys(obj).length === 0) === (JSON.stringify(obj) === "{}");
    if (!emptyKeysMeansEmptyObject(first) || !emptyKeysMeansEmptyObject(second)) {
        throw Error("Object invalid");
    }
    const keys = getKeysOfObject(first);
    if (keys.length !== Object.keys(second).length) {
        return false;
    }
    return keys.every(key => isEqual(first[key], second[key]));
}
export function isTypeOrUndefined(t, guard) {
    return t === undefined || guard(t);
}
export function isPriceFactors(t) {
    const typeGuardMap = {
        generalFactor: isNumber,
        taxesFactor: isNumber,
        transparent: isBoolean,
    };
    return isInstanceOf(t, typeGuardMap);
}
export function percentage(factor) {
    return 100. * (factor - 1.);
}
export function createSvg(s) {
    const dp = new DOMParser();
    const svg = dp.parseFromString(s, "image/svg+xml").documentElement;
    return svg;
}
